import Button from 'parkdepot-shared/components/Buttons/Button'
import { BUTTON_KIND, BUTTON_SIZE } from 'parkdepot-shared/components/Buttons/types'
import FormPlateInput from 'parkdepot-shared/components/LPInput/FormPlateInput'
import { useStyletron } from 'parkdepot-shared/theme'
import useLicensePlateValidation from 'parkdepot-shared/utils/hooks/useLicensePlateValidation'
import React, { FC, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { v1 as uuidv1 } from 'uuid'
import Text from '../../../../components/style/Text.style'
import VehicleSelect from '../../../../components/VehicleSelect/VehicleSelect.component'
import { getMultiPlateBookingsLotsMemoized } from '../../../../constants/hardcodedMultiPlateBookingsRules.consts'
import { CheckoutContext } from '../../../../context/CheckoutContext/checkout.context'
import { useGetUser } from '../../../../hooks/useAuth'
import { Checkout } from '../../../../types/checkout.interface'
import { Vehicle } from '../../../../types/vehicle.interface'
import { checkoutRouteCustomerStep } from '../../route'
import AddVehicleWrapper from '../../style/AddVehicleWrapper.style'
import NextButtonContainer from '../../style/NextButtonContainer.style'
import { LotViewName } from '../../../../types/lot.interface'
import {
    AddIcon,
    SecondVehicleContainer,
    TrashIcon,
    VehicleContainer,
    VehicleErrorContainer,
    VehiclesContainer
} from './VehicleStep.style'
import ReactDOM from 'react-dom'
import { getDomainExtension } from '@utils/getDomainExtension'

interface Props {
    bottomRef: React.RefObject<HTMLDivElement>
}

const VehicleStep: FC<Props> = ({ bottomRef }) => {
    const [, theme] = useStyletron()
    const { t } = useTranslation()
    const history = useHistory()

    const [identicalVehiclesError, setIdenticalVehiclesError] = useState(false)
    const [nextButtonEnabled, setNextButtonEnabled] = useState(false)
    const [selectedVehicles, setSelectedVehicles] = useState<Vehicle[]>([])
    const [firstLoad, setFirstLoad] = useState(true)

    const { checkout, modifyAndSetCheckout } = useContext(CheckoutContext)
    const { user } = useGetUser()
    const validateLicensePlate = useLicensePlateValidation()
    const domain = getDomainExtension()
    const defaultCountryPlate = domain === 'com' ? 'INT' : domain.toUpperCase()

    // check if user can book multiple plates on this lot && if checkout type is not subscription
    const multiPlateBookingsAllowed =
        getMultiPlateBookingsLotsMemoized().includes(checkout?.lot?.id) &&
        checkout?.lotCheckoutType !== LotViewName.SUBSCRIPTION

    useEffect(() => {
        if (firstLoad && checkout?.selectedVehicles) {
            setSelectedVehicles(checkout.selectedVehicles)
            setFirstLoad(false)
        }
    }, [checkout, firstLoad])

    useEffect(() => {
        if (selectedVehicles.length === 0) return
        const allVehiclesHavePlate = selectedVehicles.every(
            (vehicle) => vehicle.plate && validateLicensePlate(vehicle.plate ?? '-', vehicle.country ?? '').valid
        )
        if (allVehiclesHavePlate) {
            // check if duplicated plate + country
            const plates = selectedVehicles.map((vehicle) => `${vehicle.plate}-${vehicle.country}`)
            const uniquePlates = new Set(plates)
            if (plates.length !== uniquePlates.size) {
                setNextButtonEnabled(false)
                setIdenticalVehiclesError(true)
                return
            } else {
                setNextButtonEnabled(true)
            }
        } else {
            setNextButtonEnabled(false)
        }
    }, [selectedVehicles, validateLicensePlate])

    const onVehicleChange = (vehicle?: Vehicle) => {
        if (vehicle) {
            const updatedVehicle = selectedVehicles.find((v) => v.id === vehicle.id)
            if (updatedVehicle) {
                // replace vehicle
                const newVehicles = selectedVehicles.map((v) => {
                    if (v.new !== vehicle.new) {
                        return {
                            id: uuidv1(),
                            new: true,
                            country: defaultCountryPlate
                        }
                    }
                    if (v.id === vehicle.id) {
                        return vehicle
                    }
                    return v
                })

                setSelectedVehicles(newVehicles)
            } else {
                if (selectedVehicles.length < 2) {
                    setSelectedVehicles([vehicle])
                }
            }
        } else {
            setSelectedVehicles((prev) => {
                return [
                    ...prev,
                    {
                        id: uuidv1(),
                        new: true,
                        country: defaultCountryPlate
                    }
                ]
            })
        }
    }

    const onNewVehicleChange = (value: string, type: 'plate' | 'country') => {
        const newVehicle = selectedVehicles[0] || { id: uuidv1(), new: true, country: defaultCountryPlate }
        const updatedVehicle = { ...newVehicle, [type]: value }
        setSelectedVehicles([updatedVehicle])
    }

    const onVehicleRemove = (vehicleId: string) => {
        setSelectedVehicles((prev) => prev.filter((v) => v.id !== vehicleId))
    }

    const onNewVehicleClick = () => {
        if (selectedVehicles.length === 0) {
            // create two vehicles if none exist
            onVehicleChange()
        }
        onVehicleChange()
    }

    const onSubmit = (e: React.SyntheticEvent) => {
        e.preventDefault()
        if (!nextButtonEnabled) return

        modifyAndSetCheckout({
            ...checkout,
            selectedVehicles
        } as Checkout)

        history.push(checkoutRouteCustomerStep())
    }

    return (
        <div
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    onSubmit(e)
                }
            }}
        >
            <VehiclesContainer>
                {/* normal flow for non logged in users */}
                {!user && (
                    <div>
                        <FormPlateInput
                            value={(selectedVehicles && selectedVehicles[0]?.plate) || ''}
                            onChange={(pl) => onNewVehicleChange(pl, 'plate')}
                            country={(selectedVehicles && selectedVehicles[0]?.country) || defaultCountryPlate}
                            onCountryChange={(country) => onNewVehicleChange(country, 'country')}
                        />
                    </div>
                )}

                {/* user.vehicles.length === 0 */}
                {multiPlateBookingsAllowed && user && user.vehicles.length === 0 && (
                    <div>
                        <Text fontWeight={600} typography={theme.typography.ParagraphMedium}>
                            {t('checkout.firstVehicle')}
                        </Text>
                        <VehicleSelect
                            vehicle={selectedVehicles[0]}
                            hideSelect
                            selectedVehicles={selectedVehicles}
                            setSelectedVehicles={setSelectedVehicles}
                        />
                        {selectedVehicles.length > 1 && (
                            <>
                                <SecondVehicleContainer>
                                    <Text fontWeight={600} typography={theme.typography.ParagraphMedium}>
                                        {t('checkout.secondVehicle')}
                                    </Text>

                                    <TrashIcon
                                        onClick={() => onVehicleRemove(selectedVehicles[1].id || '')}
                                        className="wemolo-ico-trash"
                                    />
                                </SecondVehicleContainer>
                                <VehicleSelect
                                    vehicle={selectedVehicles[1]}
                                    hideSelect
                                    selectedVehicles={selectedVehicles}
                                    setSelectedVehicles={setSelectedVehicles}
                                />
                            </>
                        )}
                    </div>
                )}

                {/* no vehicles for logged in user */}
                {user && !multiPlateBookingsAllowed && user.vehicles.length === 0 && (
                    <div>
                        <Text fontWeight={600} typography={theme.typography.ParagraphMedium}>
                            {t('checkout.yourVehicle')}
                        </Text>
                        <VehicleSelect
                            hideSelect
                            selectedVehicles={selectedVehicles}
                            setSelectedVehicles={setSelectedVehicles}
                        />
                    </div>
                )}

                {/* no selected vehicles for logged in user */}
                {user && selectedVehicles.length === 0 && user.vehicles.length !== 0 && (
                    <div>
                        <Text fontWeight={600} typography={theme.typography.ParagraphMedium}>
                            {(!multiPlateBookingsAllowed || !user) && t('checkout.yourVehicle')}
                            {multiPlateBookingsAllowed && user && t('checkout.firstVehicle')}
                        </Text>
                        <VehicleSelect selectedVehicles={selectedVehicles} setSelectedVehicles={setSelectedVehicles} />
                    </div>
                )}

                {/* at least one vehicle selected */}
                {user &&
                    user.vehicles.length !== 0 &&
                    selectedVehicles.map((vehicle, index) => (
                        <div key={vehicle.id}>
                            <VehicleContainer>
                                <Text fontWeight={600} typography={theme.typography.ParagraphMedium}>
                                    {(!multiPlateBookingsAllowed || !user) && t('checkout.yourVehicle')}
                                    {multiPlateBookingsAllowed && user && index === 0 && t('checkout.firstVehicle')}
                                    {multiPlateBookingsAllowed && user && index > 0 && t('checkout.secondVehicle')}
                                </Text>
                                {index > 0 && (
                                    <TrashIcon
                                        className="wemolo-ico-trash"
                                        onClick={() => onVehicleRemove(vehicle.id || '')}
                                    />
                                )}
                            </VehicleContainer>
                            <VehicleSelect
                                vehicle={vehicle}
                                selectedVehicles={selectedVehicles}
                                setSelectedVehicles={setSelectedVehicles}
                            />
                        </div>
                    ))}
            </VehiclesContainer>
            {/* add another vehicle button */}
            {multiPlateBookingsAllowed &&
                user &&
                selectedVehicles.length < 2 &&
                checkout?.lotCheckoutType !== LotViewName.SUBSCRIPTION && (
                    <AddVehicleWrapper>
                        <Button
                            id="add-another-vehicle-btn"
                            type="button"
                            onClick={onNewVehicleClick}
                            $size={BUTTON_SIZE.medium}
                            $kind={BUTTON_KIND.ghost}
                            startEnhancer={() => <AddIcon className="wemolo-car-add" />}
                            overrides={{
                                BaseButton: {
                                    style: {
                                        width: '100%'
                                    }
                                }
                            }}
                        >
                            <span>{t('checkout.addAnotherVehicle')}</span>
                        </Button>
                    </AddVehicleWrapper>
                )}
            {bottomRef.current &&
                ReactDOM.createPortal(
                    <NextButtonContainer>
                        {identicalVehiclesError && (
                            <VehicleErrorContainer>{t('errors.identicalVehiclesError')}</VehicleErrorContainer>
                        )}
                        <Button
                            id="vehicle-step-next-button"
                            data-testid="continue-to-customer-step-button"
                            onClick={onSubmit}
                            disabled={!nextButtonEnabled}
                            $size={BUTTON_SIZE.medium}
                            $kind={BUTTON_KIND.primary}
                            overrides={{
                                BaseButton: {
                                    style: {
                                        width: '100%'
                                    }
                                }
                            }}
                        >
                            <div>{t('checkout.payment.nextStep')}</div>
                        </Button>
                    </NextButtonContainer>,
                    bottomRef.current
                )}
        </div>
    )
}

export default VehicleStep
